import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../../utils/axios/axiosConfig";
import { toast } from "react-toastify";
import { DeleteModal } from "../../components";
import { useAccessChecker } from "../../utils/Functions/Authorization";
import { Star as StarIcon } from "lucide-react";
import { MagnifyingGlass, Plus, PencilSimple, Trash, ChatCircleText } from "@phosphor-icons/react";
import {
  PageHeader, Button, Input, Table, THead, TBody, TR, TH, TD,
  Skeleton, EmptyState, Pagination, Tooltip,
} from "../../components/ui";
import ReviewModal from "./ReviewModal";

function Stars({ value = 0 }) {
  return (
    <div className="inline-flex items-center">
      {[1, 2, 3, 4, 5].map((i) => (
        <StarIcon
          key={i}
          className={`h-4 w-4 ${i <= value ? "text-yellow-400" : "text-faint"}`}
          style={i <= value ? { fill: "#facc15" } : {}}
        />
      ))}
    </div>
  );
}

export default function Reviews() {
  const { checkAccess } = useAccessChecker();

  const [reviewModal, setReviewModal] = useState(false);
  const [reviewToUpdate, setReviewToUpdate] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [reviewId, setReviewId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchReviews(1), 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, done]);

  useEffect(() => {
    fetchReviews(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchReviews = async (page = 1) => {
    try {
      setLoading(true);
      const res = await axiosInstance.post(`/reviews/search?page=${page}&per_page=10`, {
        searchValue: searchValue || "",
      });
      const data = res?.data;
      setReviews(data?.data || []);
      setCurrentPage(data?.current_page || 1);
      setTotalPages(data?.last_page || 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "2-digit" });

  return (
    <div className="w-full p-6 text-start">
      <PageHeader
        title="Reviews"
        subtitle="Moderate customer product reviews."
        actions={
          <>
            <Input
              icon={MagnifyingGlass}
              placeholder="Search reviews…"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              containerClassName="w-full sm:w-64"
            />
            {checkAccess("reviews", "create") && (
              <Button icon={Plus} onClick={() => setReviewModal(true)}>
                New review
              </Button>
            )}
          </>
        }
      />

      <Table>
        <THead sticky>
          <TR className="hover:bg-transparent">
            <TH>Client</TH>
            <TH>Product</TH>
            <TH>Rating</TH>
            <TH>Comment</TH>
            <TH>Date</TH>
            <TH className="text-right">Actions</TH>
          </TR>
        </THead>
        <TBody>
          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <TR key={i} className="hover:bg-transparent">
                <TD><Skeleton className="h-3.5 w-28" /></TD>
                <TD><div className="flex items-center gap-3"><Skeleton className="h-10 w-10 rounded-lg" /><Skeleton className="h-3.5 w-28" /></div></TD>
                <TD><Skeleton className="h-3.5 w-20" /></TD>
                <TD><Skeleton className="h-3.5 w-40" /></TD>
                <TD><Skeleton className="h-3.5 w-24" /></TD>
                <TD><Skeleton className="ml-auto h-7 w-16" /></TD>
              </TR>
            ))}

          {!loading &&
            reviews.map((review) => (
              <TR key={review.id}>
                <TD className="font-medium text-ink">
                  {review.user?.fullName || review.author || "—"}
                </TD>
                <TD>
                  <div className="flex items-center gap-3">
                    {review.product?.images?.[0] ? (
                      <img src={review.product.images[0]} alt="" className="h-10 w-10 rounded-lg border border-line object-cover" />
                    ) : (
                      <div className="h-10 w-10 rounded-lg border border-line bg-surface-2" />
                    )}
                    <span className="line-clamp-1 text-ink">{review.product?.name || "—"}</span>
                  </div>
                </TD>
                <TD>
                  <div className="flex items-center gap-2">
                    <Stars value={review.rating} />
                    <span className="text-muted">{review.rating || 0}</span>
                  </div>
                </TD>
                <TD className="text-muted">
                  <span className="line-clamp-1 max-w-[320px]">{review.comment}</span>
                </TD>
                <TD className="text-muted">{review.created_at ? formatDate(review.created_at) : "—"}</TD>
                <TD>
                  <div className="flex items-center justify-end gap-0.5">
                    {checkAccess("reviews", "update") && (
                      <Tooltip label="Edit">
                        <button
                          onClick={() => {
                            setReviewToUpdate(review);
                            setReviewModal(true);
                          }}
                          className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink"
                        >
                          <PencilSimple size={16} />
                        </button>
                      </Tooltip>
                    )}
                    {checkAccess("reviews", "delete") && (
                      <Tooltip label="Delete">
                        <button
                          onClick={() => {
                            setReviewId(review.id);
                            setDeleteModal(true);
                          }}
                          className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-red-500/10 hover:text-red-600"
                        >
                          <Trash size={16} />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </TD>
              </TR>
            ))}

          {!loading && reviews.length === 0 && (
            <tr>
              <td colSpan={6}>
                <EmptyState
                  icon={ChatCircleText}
                  title="No reviews found"
                  description="Customer reviews will appear here once submitted."
                />
              </td>
            </tr>
          )}
        </TBody>
      </Table>

      <Pagination
        className="mt-4"
        page={currentPage}
        totalPages={totalPages}
        onChange={(p) => setCurrentPage(p)}
        summary={`Page ${currentPage} of ${totalPages}`}
      />

      <ReviewModal
        isOpen={reviewModal}
        onClose={() => setReviewModal(false)}
        ReviewToUpdate={reviewToUpdate}
        setReviewToUpdate={setReviewToUpdate}
        setDone={setDone}
        done={done}
      />
      <DeleteModal
        setDeleteId={setReviewId}
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={reviewId}
        Model="reviews"
        setDone={setDone}
        setLoding={setLoading}
        done={done}
      />
    </div>
  );
}
